'use client';
import { createContext, useState, useCallback, useEffect } from 'react';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import WindowSize from '@/utils/windowSize';

export const HeaderUIStateContext = createContext();

function detectTouchScreen() {
  if (typeof window === 'undefined') return false;

  const hasTouchPoints = navigator.maxTouchPoints > 0;
  const isCoarsePointer = matchMedia('(pointer:coarse)').matches;

  return hasTouchPoints && isCoarsePointer;
}

function HeaderUIStateProvider({ children }) {
  const pathname = useAppPathname();
  const windowSize = WindowSize();
  const [isDesktop, setIsDesktop] = useState(false);
  const [showUtility, setShowUtility] = useState(true);
  const [isHomePage, setIsHomePage] = useState(true);
  const [navHoverItem, setNavHoverItem] = useState(undefined);
  const hasTouchScreen = detectTouchScreen();

  const handleNavHover = useCallback(
    (e) => {
      const target = e?.target || undefined;
      let newNavHoverItem;

      if (target?.nodeName === 'SPAN') {
        newNavHoverItem = target.parentElement.parentElement;
      } else if (target?.nodeName === 'LI') {
        newNavHoverItem = target;
      }

      if (hasTouchScreen && newNavHoverItem === navHoverItem) {
        newNavHoverItem = undefined;
      }

      setNavHoverItem(newNavHoverItem);

      // Fix: check aria-expanded attribute directly on the nav item
      const isSubnavItem =
        newNavHoverItem?.classList?.contains('subnav') &&
        newNavHoverItem?.querySelector('[aria-expanded="true"]') !== null;

      if (isSubnavItem) {
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        document.body.classList.add('overflow-hidden');
        document.body.style.paddingRight =
          scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
      } else {
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
    },
    [hasTouchScreen, navHoverItem]
  );

  const setScrollLock = useCallback((lock = true) => {
    const isLocked = document.body.classList.contains('overflow-hidden');

    // If already in desired state, do nothing
    if (lock && isLocked) return;
    if (!lock && !isLocked) return;

    const openSubnav = document.querySelector(
      'li.subnav [aria-expanded="true"]:not([aria-expanded="false"])'
    );
    const isSubnavItem = !!openSubnav;

    if (isSubnavItem && lock) {
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.classList.add('overflow-hidden');
      document.body.style.paddingRight =
        scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    } else {
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
    }
  }, []);

  useEffect(() => {
    setIsDesktop(windowSize.width > 1200);
  }, [windowSize]);

  useEffect(() => {
    setIsHomePage(pathname === '/');
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setShowUtility(window.pageYOffset <= 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <HeaderUIStateContext.Provider
      value={{
        isDesktop,
        hasTouchScreen,
        showUtility,
        isHomePage,
        navHoverItem,
        handleNavHover,
        setScrollLock
      }}
    >
      {children}
    </HeaderUIStateContext.Provider>
  );
}

export default HeaderUIStateProvider;
